import { Injectable } from '@angular/core';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import { Actions, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { MessageTypeToTemplateMapping, Template } from '@quorum/models/xs-resource';
import { messageTypeToTemplateMappingActions, templateActions } from '@quorum/sha-entities/quorum';
import { setSelectedLanguage } from './templates.actions';
import { Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import * as fromTemplates from './templates.reducer';
import { templatesQuery } from './templates.selectors';
@Injectable()
export class TemplatesFacade {
  snackBarSubject$ = new Subject<{ message: string; action?: string; config?: MatSnackBarConfig<any> }>();
  loadingComplete$ = this.store.pipe(select(templatesQuery.isLoadingComplete));
  storeGallery$ = this.store.pipe(select(templatesQuery.storeGalleryTemplates));
  quorumGallery$ = this.store.pipe(select(templatesQuery.quorumGalleryTemplates));
  quorumLibrary$ = this.store.pipe(select(templatesQuery.quorumLibraryTemplates));
  messageTypes$ = this.store.pipe(select(templatesQuery.messageTypes));
  messageTypeToTemplateMappings$ = this.store.pipe(select(templatesQuery.messageTypeToTemplateMappings));
  selectedTemplate$ = this.store.pipe(select(templatesQuery.selectedTemplate));
  selectedLanguage$ = this.store.pipe(select(templatesQuery.selectedLanguage));
  languages$ = this.store.pipe(select(templatesQuery.languages));
  storeGalleryByMessageType$ = this.store.pipe(select(templatesQuery.galleryByMessageType));
  storeGalleryByMessageTypeWithDefaults$ = this.store.pipe(select(templatesQuery.galleryByMessageTypeWithDefaults));
  supportedLanguages$ = this.store.pipe(
    select(templatesQuery.storeGalleryTemplates),
    map((templates) => {
      return templates
        .map((template) => Object.keys(template.languageDefinitionMap))
        .reduce((prev, curr) => prev.concat(curr), [])
        .filter((value: any, index: any, self: any) => self.indexOf(value) === index);
    })
  );

  quorumLibrarySupportedLanguages$ = this.store.pipe(
    select(templatesQuery.quorumLibraryTemplates),
    map((templates) => {
      return templates
        .map((template) => Object.keys(template.languageDefinitionMap))
        .reduce((prev, curr) => prev.concat(curr), [])
        .filter((value: any, index: any, self: any) => self.indexOf(value) === index);
    })
  );

  createOrUpdateMessageTypeToTemplateMapping$ = this.actions$.pipe(
    ofType(
      messageTypeToTemplateMappingActions.CreateMessageTypeToTemplateMappingOnServerSuccess,
      messageTypeToTemplateMappingActions.UpdateMessageTypeToTemplateMappingOnServerSuccess
    ),
    tap((action) => {
      console.log(action);
    })
  );

  constructor(private actions$: Actions, private store: Store<fromTemplates.TemplatesPartialState>) {}

  dispatch(action: Action) {
    this.store.dispatch(action);
  }

  addToast(toastObject: { message: string; action?: string; config?: MatSnackBarConfig<any> }) {
    toastObject.config = {
      duration: 3000,
      horizontalPosition: 'start',
      ...toastObject.config,
    };

    this.snackBarSubject$.next(toastObject);
  }

  selectTemplate(custId: string, templateId: string) {
    this.dispatch(templateActions.GetStoreGalleryTemplateFromServer({ custId: custId, id: templateId }));
  }

  selectQuorumLibraryTemplate(templateId: string) {
    this.dispatch(templateActions.GetQuorumLibraryTemplateFromServer({ id: templateId }));
  }

  selectQuorumGalleryTemplate(templateId: string) {
    this.dispatch(templateActions.GetQuorumGalleryTemplateFromServer({ id: templateId }));
  }

  selectLanguage(language: string) {
    this.dispatch(setSelectedLanguage({ language: language }));
  }

  // updateSelectedTemplate(etag: string, template: Template) {}

  getMessageTypeToTemplateMappings(custId: string) {
    this.dispatch(messageTypeToTemplateMappingActions.GetMessageTypeToTemplateMappingsFromServer({ custId: custId }));
  }

  createMessageTypeToTemplateMapping(messageTypeToTemplateMapping: MessageTypeToTemplateMapping) {
    this.dispatch(
      messageTypeToTemplateMappingActions.CreateMessageTypeToTemplateMappingOnServer({
        custId: messageTypeToTemplateMapping.custId,
        messageTypeId: messageTypeToTemplateMapping.messageTypeId,
        messageTypeToTemplateMapping: messageTypeToTemplateMapping,
      })
    );
  }

  updateMessageTypeToTemplateMapping(messageTypeToTemplateMapping: MessageTypeToTemplateMapping) {
    this.dispatch(
      messageTypeToTemplateMappingActions.UpdateMessageTypeToTemplateMappingOnServer({
        custId: messageTypeToTemplateMapping.custId,
        messageTypeId: messageTypeToTemplateMapping.messageTypeId,
        messageTypeToTemplateMapping: messageTypeToTemplateMapping,
      })
    );
  }

  patchMessageTypeToTemplateMapping(messageTypeToTemplateMapping: MessageTypeToTemplateMapping) {
    this.dispatch(
      messageTypeToTemplateMappingActions.PatchMessageTypeToTemplateMappingOnServer({
        custId: messageTypeToTemplateMapping.custId,
        messageTypeId: messageTypeToTemplateMapping.messageTypeId,
        messageTypeToTemplateMapping: messageTypeToTemplateMapping,
      })
    );
  }

  updateSelectedTemplate(custId: string, etag: string, template: Template) {
    this.dispatch(templateActions.UpdateStoreGalleryTemplateOnServer({ custId, etag, template }));
  }

  updateSelectedQuorumLibraryTemplate(etag: string, template: Template) {
    this.dispatch(templateActions.UpdateQuorumLibraryTemplateOnServer({ etag, template }));
  }

  updateSelectedQuorumGalleryTemplate(etag: string, template: Template) {
    this.dispatch(templateActions.UpdateQuorumGalleryTemplateOnServer({ etag, template }));
  }

  deleteSelectedTemplate(custId: string, templateId: string) {
    this.dispatch(templateActions.DeleteStoreGalleryTemplateOnServer({ custId: custId, templateId: templateId }));
  }
  // getStoreGalleryTemplates(custId: string) {
  //   this.dispatch(templateActions.GetStoreGalleryTemplatesFromServer({ custId: custId }));
  // }

  // getStoreGalleryTemplate(custId: string, templateId: string) {
  //   this.dispatch(templateActions.GetStoreGalleryTemplateFromServer({ custId: custId, id: templateId }));
  // }

  createStoreGalleryTemplate(custId: string, template: Template) {
    this.dispatch(templateActions.CreateStoreGalleryTemplateOnServer({ custId: custId, template: template }));
  }

  // updateStoreGalleryTemplate(custId: string, template: Template) {
  //   this.dispatch(templateActions.UpdateStoreGalleryTemplateOnServer({ custId: custId, template: template }));
  // }

  // deleteStoreGalleryTemplate(custId: string, templateId: string) {
  //   this.dispatch(templateActions.DeleteStoreGalleryTemplateOnServer({ custId: custId, templateId: templateId }));
  // }

  // getQuorumGalleryTemplates(custId: string) {
  //   this.dispatch(templateActions.GetQuorumGalleryTemplatesFromServer({}));
  // }
}
